import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form } from '@ant-design/compatible';
import { Drawer, Button, Input, Space, Row, Col, InputNumber, Spin, Popconfirm, Table, DatePicker, Alert, Tooltip, Select, message, Modal } from 'antd';
import {
    BankOutlined,
    CreditCardOutlined,
    DeleteOutlined,
    PaperClipOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
    UserOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
import { _newLine, paymentMethodOptions, isValidDateForm, serializeDateTime, renderMoney } from '@/utils/helpers';
import { createReceipt, getCustomerDepositSummary, previewReceiptMutation, updateReceipt } from '@/redux/actions/receiptActions';
import { fetchCustomer } from '@/redux/actions/customerActions';
import { fetchProduct } from '@/redux/actions/productActions';
import moment from 'moment';
import CustomerSelect from '@/components/customers/CustomerSelect';
import ProductSelect from '@/components/products/ProductSelect';
import ImageSelector from '@/components/ImageSelector';
import { checkPermission } from '@/utils/permission';
import { getSelectedDepositBalance } from '@/components/customers/customerDepositAllocation';
import {
    appendCustomerServices,
    buildEditableReceiptItems,
    buildReceiptPaymentBreakdowns,
    buildReceiptMutationControlFields,
    buildReceiptMutationConfirmationSummary,
    calculateEditableBalance,
    calculateReceiptExternalAmount,
    calculateReceiptSettlement,
    getAvailableCustomerServices,
    getReceiptItemValidationMessage,
    hasReceiptInternalPaymentSource,
    mergeEditableDeposits,
} from './receiptMutationPresentation';
import ReceiptItemInfoCell from './ReceiptItemInfoCell';
import './ReceiptForm.less';

const fieldLabel = (label, guide) => <Tooltip title={guide}><span>{label} <QuestionCircleOutlined style={{ color: '#8c8c8c' }} /></span></Tooltip>;
const maxPaymentMethods = paymentMethodOptions.length;

const ReceiptSection = ({ icon, title, description, extra, financial, children }) => (
    <section className={`receipt-form-section${financial ? ' receipt-form-section--financial' : ''} mb-3`}>
        <div className="receipt-form-section__header">
            <div className="receipt-form-section__heading">
                <span className="receipt-form-section__icon">{icon}</span>
                <div>
                    <div className="receipt-form-section__title">{title}</div>
                    {description && <div className="receipt-form-section__description">{description}</div>}
                </div>
            </div>
            {extra && <div className="receipt-form-section__extra">{extra}</div>}
        </div>
        <div className="receipt-form-section__body">{children}</div>
    </section>
);

const PaymentSourceBox = ({ icon, title, available, tone, children, target, remaining }) => (
    <section className={`receipt-payment-source receipt-payment-source--${tone}`}>
        <div className="receipt-payment-source__header">
            <div className="receipt-payment-source__title">
                <span className="receipt-payment-source__icon">{icon}</span>
                <span>{title}</span>
            </div>
            <div className="receipt-payment-source__available">
                <span>Có</span>
                <strong>{renderMoney(available)}</strong>
            </div>
        </div>

        <div className="receipt-payment-source__body">{children}</div>

        <div className="receipt-payment-source__summary">
            <div>
                <span>Áp dụng cho:</span>
                <strong>{target}</strong>
            </div>
            <div>
                <span>Còn lại:</span>
                <strong>{renderMoney(remaining)}</strong>
            </div>
        </div>
    </section>
);

class ReceiptForm extends Component {
    constructor() {
        super();
        this.state = {
            isLoading: false,
            submitting: false,
            items: [],
            images: [],
            net_total: 0,
            payment_breakdowns: [{ key: `payment-${Date.now()}`, payment_method: null, amount: 0, provider_name: 'VNĐ' }],
            customer_debt: 0,
            customer_balance: 0,
            deposit_data: {},
            available_customer_services: [],
            selected_customer_service_ids: [],
            service_options_loading: false,
            special_discount: 0,
            receiptRequestKey: `receipt-${Date.now()}-${Math.random().toString(36).slice(2)}`
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const isOpening = nextProps.visible && !this.props.visible;
        const isChangingReceipt = nextProps.visible
            && nextProps.currentReceipt !== this.props.currentReceipt;

        if (isOpening || isChangingReceipt) {
            this.initializeFormData(nextProps.currentReceipt);
        }
    }

    initializeFormData = (currentReceipt) => {
        this.props.form.resetFields();

        if (!currentReceipt) {
            return;
        }

        const items = buildEditableReceiptItems(currentReceipt);
        const breakdowns = (currentReceipt.payment_breakdowns || currentReceipt.paymentBreakdowns || [])
            .map(row => ({ ...row, key: row.id || `payment-${Date.now()}-${Math.random()}` }));

        // Mỗi lần mở form edit đều bind lại từ phiếu hiện tại vì state đã được dọn khi đóng form.
        this.setState({
            items,
            net_total: Number(currentReceipt.amount_paid || 0),
            special_discount: Number(currentReceipt.special_discount || 0),
            payment_breakdowns: breakdowns.length ? breakdowns : [{
                key: `payment-${Date.now()}`,
                payment_method: Number(currentReceipt.amount_paid || 0) > 0
                    ? currentReceipt.payment_method
                    : null,
                amount: Number(currentReceipt.amount_paid || 0),
                provider_name: 'VNĐ',
            }],
        }, () => this.loadAvailableCustomerServices(currentReceipt));
    };

    confirmFinancialMutation = preview => new Promise((resolve, reject) => {
        const summary = buildReceiptMutationConfirmationSummary(preview);
        Modal.confirm({
            title: 'Xác nhận phân bổ lại dữ liệu tài chính',
            width: isMobile ? '100%' : '55%',
            maskClosable: false,
            content: (
                <div>
                    {summary.changedCount > 0
                        ? <p>Thay đổi này ảnh hưởng {summary.changedCount} dòng phiếu thu.</p>
                        : <p>Số tiền hoặc thông tin thanh toán của phiếu sẽ được cập nhật.</p>}
                    <p>Hệ thống sẽ đảo {renderMoney(summary.productReversal)} thanh toán sản phẩm và {renderMoney(summary.serviceReversal)} phân bổ dịch vụ, sau đó đối soát lại theo nội dung mới.</p>
                    {summary.depositReversal > 0 && <p>Tiền cọc hoàn nguyên: <strong>{renderMoney(summary.depositReversal)}</strong>.</p>}
                    {summary.balanceReversal > 0 && <p>Số dư hoàn nguyên: <strong>{renderMoney(summary.balanceReversal)}</strong>.</p>}
                    {summary.completedRefundAmount > 0 && (
                        <p>Khoản hoàn được bảo toàn: <strong>{renderMoney(summary.completedRefundAmount)}</strong>.</p>
                    )}
                    {summary.projectedReceivableAdjustment > 0 && (
                        <p>
                            Điều chỉnh công nợ dự kiến:{' '}
                            <strong>
                                {summary.projectedReceivableDirection === 'CREDIT' ? 'giảm ' : 'tăng '}
                                {renderMoney(summary.projectedReceivableAdjustment)}
                            </strong>
                            {' '}(công nợ mục tiêu từ phiếu: {renderMoney(summary.projectedReceivableTarget)}).
                        </p>
                    )}
                    {summary.warnings.map((warning, index) => (
                        <Alert key={index} type="warning" message={warning} className="mb-2" />
                    ))}
                </div>
            ),
            okText: 'Xác nhận và lưu',
            cancelText: 'Xem lại',
            onOk: () => resolve(true),
            onCancel: () => reject(new Error('CANCELLED')),
        });
    });

    async submit(e) {
        e.preventDefault();
        this.props.form.validateFields(async (err, values) => {
            if (!err) {
                const hasNegativePayment = this.state.payment_breakdowns.some(row => Number(row.amount || 0) < 0);
                if (hasNegativePayment) {
                    message.error('Số tiền của hình thức thu không được nhỏ hơn 0.', 5);
                    return;
                }
                const enteredPaymentRows = buildReceiptPaymentBreakdowns(this.state.payment_breakdowns);
                if (enteredPaymentRows.length > maxPaymentMethods) {
                    message.error(`Mỗi phiếu chỉ được tối đa ${maxPaymentMethods} hình thức thu.`, 5);
                    return;
                }
                const enteredMethods = enteredPaymentRows.map(row => row.payment_method).filter(Boolean);
                if (new Set(enteredMethods).size !== enteredMethods.length) {
                    message.error('Mỗi hình thức thu chỉ được chọn một lần.', 5);
                    return;
                }
                const invalidPayment = enteredPaymentRows.some(row => !row.payment_method);
                if (invalidPayment) {
                    message.error('Vui lòng chọn hình thức và nhập số tiền lớn hơn 0 cho từng dòng.', 5);
                    return;
                }
                const itemValidationMessage = getReceiptItemValidationMessage(this.state.items);
                if (itemValidationMessage) {
                    message.error(itemValidationMessage, 5);
                    return;
                }
                this.setState({ submitting: true, isLoading: true });
                try {
                    const { items, images } = this.state;

                    const amountPaid = enteredPaymentRows
                        .reduce((sum, row) => sum + Number(row.amount || 0), 0);
                    const requestedBalanceAmount = Math.max(0, Number(values.balance_amount || 0));
                    const requestedDepositAmount = Math.max(0, Number(values.deposit_amount || 0));
                    const settlement = calculateReceiptSettlement({
                        externalAmount: amountPaid,
                        productAmount: this.getProductAmount(items),
                        serviceAmount: this.getServiceAmount(items),
                        specialDiscount: values.special_discount,
                        balanceAmount: requestedBalanceAmount,
                        depositAmount: requestedDepositAmount,
                    });
                    if (requestedBalanceAmount + requestedDepositAmount > settlement.internalLimit) {
                        message.error('Tổng tiền cọc và số dư đang vượt phần sản phẩm/dịch vụ còn phải thanh toán.', 5);
                        this.setState({ submitting: false, isLoading: false });
                        return;
                    }

                    values.items = items;
                    values.payment_at = serializeDateTime(values.payment_at, 'date');
                    values.payment_breakdowns = enteredPaymentRows;
                    values.amount_paid = values.payment_breakdowns.reduce((sum, row) => sum + Number(row.amount || 0), 0);
                    values.net_total = values.amount_paid;
                    values.settlement_total = settlement.settlementTotal;
                    delete values.payment_method;
                    const depositAmount = Math.max(0, Number(values.deposit_amount) || 0);
                    values.deposit_application = this.props.currentReceipt && checkPermission('customer_deposit', 'apply') ? {
                        mode: 'REPLACE',
                        deposit_ids: depositAmount > 0 ? values.deposit_ids : [],
                        amount: depositAmount,
                        reason: values.deposit_reason,
                    } : (depositAmount > 0 ? {
                        deposit_ids: values.deposit_ids,
                        amount: depositAmount,
                        reason: values.deposit_reason,
                    } : null);
                    const balanceAmount = Math.max(0, Number(values.balance_amount) || 0);
                    values.balance_customer_service_id = values.balance_customer_service_id || 'ALL'; // Mạc định là áp dụng cho tất cả dịch vụ nếu không chọn dịch vụ cụ thể.
                    const applyBalanceToAll = values.balance_customer_service_id === 'ALL';
                    values.balance_application = this.props.currentReceipt ? {
                        mode: 'REPLACE',
                        amount: balanceAmount,
                        customer_service_id: applyBalanceToAll ? null : values.balance_customer_service_id,
                        target: applyBalanceToAll ? 'ALL' : 'SERVICE',
                        reason: values.balance_reason,
                    } : (balanceAmount > 0 ? {
                        amount: balanceAmount,
                        customer_service_id: applyBalanceToAll ? null : values.balance_customer_service_id,
                        target: applyBalanceToAll ? 'ALL' : 'SERVICE',
                        reason: values.balance_reason,
                    } : null);
                    delete values.balance_amount;
                    delete values.balance_customer_service_id;
                    delete values.balance_reason;
                    delete values.deposit_ids;
                    delete values.deposit_amount;
                    delete values.deposit_reason;

                    let finalImages = images;
                    if (this.props.currentReceipt) {
                        const oldImages = Array.isArray(this.props.currentReceipt.images)
                            ? this.props.currentReceipt.images
                            : [];

                        // nếu images đang rỗng thì lấy oldImages
                        if (!finalImages || finalImages.length === 0) {
                            finalImages = oldImages.map((img) => ({
                                manual_id: img.manual_id ?? img.id ?? img.uid ?? null,
                                src: img.src ?? img.url ?? img.originalImage ?? img,
                            })).filter(x => x.src);
                        }
                    }

                    values.images = finalImages;

                    if (this.props.currentReceipt) {
                        values.customer_id = this.props.currentReceipt.customer_id;
                        values.commission_revision = this.props.currentReceipt.commission_revision || 1;
                        const preview = await this.props.previewReceiptMutation(this.props.currentReceipt.id, values);
                        if (preview.requires_confirmation) {
                            await this.confirmFinancialMutation(preview);
                        }
                        const mutationKey = `receipt-mutation-${this.props.currentReceipt.id}-${values.commission_revision}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                        Object.assign(values, buildReceiptMutationControlFields(preview, mutationKey));
                        await this.props.updateReceipt(this.props.currentReceipt.id, values).then((res) => {
                            return Promise.resolve(this.props.reloadData()).then(() => this.onClose());
                        }).catch(error => {
                            this.setState({ submitting: false, isLoading: false });
                        });

                    } else {
                        values.idempotency_key = this.state.receiptRequestKey;
                        await this.props.createReceipt(values).then((res) => {
                            this.setState({ receiptRequestKey: `receipt-${Date.now()}-${Math.random().toString(36).slice(2)}` });
                            this.props.reloadData();
                            this.onClose();
                        }).catch(error => {
                            this.setState({ submitting: false, isLoading: false });
                        });

                    }

                } catch (error) {
                    this.setState({ submitting: false, isLoading: false });
                }
            }
        });
    }

    onClose() {
        this.props.onCancel();
        this.props.form.resetFields();
        this.setState({
            isLoading: false,
            submitting: false,
            images: [],
            customer_debt: 0,
            customer_balance: 0,
            deposit_data: {},
            available_customer_services: [],
            selected_customer_service_ids: [],
            service_options_loading: false,
            items: [],
            net_total: 0,
            special_discount: 0,
            payment_breakdowns: [{ key: `payment-${Date.now()}`, payment_method: null, amount: 0, provider_name: 'VNĐ' }],
        });
    }

    renderNewItem(items = [], shouldRecalculate = true) {
        let new_items = items.map((item, index) => {
            return {
                id: item.id,
                key: item.key || `${item.type}-${item.id}`,
                receipt_item_id: item.receipt_id ? item.id : item.receipt_item_id,
                origin_id: item.origin_id,
                product_sale_id: item.product_sale_id,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                line_total: Number(item.price || 0) * Number(item.quantity || 0),
                amount: item.type === 'Sản phẩm' ? item.quantity * item.price : item.amount,
                amount_to_pay: item.amount_to_pay,
                outstanding_amount: item.outstanding_amount,
                allocated_amount: item.allocated_amount,
                effective_allocated_amount: item.effective_allocated_amount,
                refunded_allocated_amount: item.refunded_allocated_amount,
                allocation_scope: item.allocation_scope,
                type: item.type,
                customer_service_id: item.customer_service_id,
                selected_at: item.selected_at || null,
            }
        })
        this.setState({
            items: new_items.filter((v, i, a) => a.findIndex(v2 => v2.key === v.key) === i)
        }, () => {
            if (shouldRecalculate) {
                this.calculateTotalAmount(); // Gọi sau khi setState để đảm bảo rằng state đã được cập nhật
            }
        });
    }

    onDelete(key) {
        let items = this.state.items.filter(item => {
            return item.key !== key;
        });
        this.renderNewItem(items)
    }

    async onChangeItem(name, value, itemKey) {
        let items = [...this.state.items];
        let index = items.findIndex(item => item.key === itemKey);
        let currentItem = items[index];
        currentItem[name] = value;
        items[index] = currentItem;
        this.renderNewItem(items)
    }

    onChangeProduct(id) {
        this.setState({ isloading: true })
        this.props.fetchProduct(id).then((item) => {

            let new_items = {
                id: item.id,
                key: `Sản phẩm-${item.id}`,
                origin_id: item.id,
                name: item.name,
                price: item.sale_price,
                quantity: 1,
                amount: item.sale_price * 1,
                type: 'Sản phẩm'
            }
            this.setState({
                items: [...this.state.items, new_items].filter((v, i, a) => a.findIndex(v2 => v2.key === v.key) === i),
                isloading: false
            }, () => {
                this.calculateTotalAmount(); // Gọi sau khi setState để đảm bảo rằng state đã được cập nhật
            });
        }).catch(err => {
            this.setState({ isloading: false })
        })
        this.calculateTotalAmount();
    }

    loadAvailableCustomerServices = currentReceipt => {
        if (!currentReceipt?.customer_id) return;
        this.setState({ service_options_loading: true });
        const customerRequest = this.props.fetchCustomer(currentReceipt.customer_id);
        const depositRequest = checkPermission('customer_deposit', 'apply')
            ? this.props.getCustomerDepositSummary(currentReceipt.customer_id)
            : Promise.resolve({});
        Promise.all([customerRequest, depositRequest]).then(([customer, depositData]) => {
            const currentApplication = currentReceipt.deposit_application || {};
            const currentBalanceApplication = currentReceipt.balance_application || {};
            const editableDeposits = mergeEditableDeposits(depositData?.active_deposits || [], currentApplication);
            this.setState({
                available_customer_services: customer?.active_purchased_services || [],
                customer_balance: Number(customer?.unallocated_balance || 0),
                deposit_data: { ...(depositData || {}), active_deposits: editableDeposits },
                service_options_loading: false,
            }, () => {
                const currentDepositIds = (currentApplication.deposit_ids || []).map(Number);
                this.props.form.setFieldsValue({
                    deposit_ids: currentDepositIds.length
                        ? currentDepositIds
                        : (editableDeposits.length === 1 ? [Number(editableDeposits[0].id)] : []),
                    deposit_amount: Number(currentApplication.amount || 0),
                    balance_amount: Number(currentBalanceApplication.amount || 0),
                    balance_customer_service_id: currentBalanceApplication.target === 'SERVICE'
                        ? Number(currentBalanceApplication.customer_service_id || 0)
                        : 'ALL',
                });
            });
        }).catch(() => this.setState({ service_options_loading: false }));
    };

    addSelectedCustomerServices = () => {
        const nextItems = appendCustomerServices(
            this.state.items,
            this.state.available_customer_services,
            this.state.selected_customer_service_ids,
            { allocationScope: 'RECEIPT' }
        );
        this.setState({ selected_customer_service_ids: [] });
        this.renderNewItem(nextItems);
    };

    onChangeCustomer(id) {
        this.setState({ isLoading: true, deposit_data: {} });
        this.props.form.resetFields([
            'balance_amount',
            'balance_customer_service_id',
            'deposit_ids',
            'deposit_amount',
        ]);
        this.props.fetchCustomer(id).then((item) => {

            const receiptExportItems = item.receipt_export || [];
            const outstandingProductSales = item.outstanding_product_sales || [];
            const services = (item.active_purchased_services || []).filter(service => Number(service.outstanding_amount || 0) > 0);
            // Xử lý receiptExport
            const receiptExportMapped = receiptExportItems.map((item) => ({
                id: item.id,
                key: `Sản phẩm-${item.id}`,
                origin_id: item.product_id,
                name: item.name,
                type: 'Sản phẩm',
                price: item.sale_price,
                quantity: 1,
                amount: item.quantity * item.sale_price,
                line_total: item.quantity * item.sale_price,
            }));
            const outstandingProductMapped = outstandingProductSales.map((sale) => ({
                id: `product-sale-${sale.id}`,
                key: `Sản phẩm-còn-lại-${sale.id}`,
                product_sale_id: sale.id,
                origin_id: sale.product_id,
                name: sale.name,
                type: 'Sản phẩm',
                price: Number(sale.price || sale.unit_price || 0),
                quantity: Number(sale.quantity || 0),
                amount: Number(sale.amount || sale.sale_amount || 0),
                amount_to_pay: Number(sale.product_outstanding_amount || sale.outstanding_amount || 0),
                allocated_amount: Math.max(0,
                    Number(sale.amount || sale.sale_amount || 0)
                    - Number(sale.product_outstanding_amount || sale.outstanding_amount || 0)
                ),
                allocation_scope: 'CURRENT_TOTAL',
                line_total: Number(sale.price || sale.unit_price || 0) * Number(sale.quantity || 0),
            }));

            // Xử lý services_selected
            const servicesMapped = services.map((service) => ({
                id: service.id,
                key: `Dịch vụ-${service.id}`,
                origin_id: service.service_id,
                name: service.service?.name ?? null,
                type: 'Dịch vụ',
                price: service.sale_price,
                quantity: service.quantity,
                amount: service.outstanding_amount,
                outstanding_amount: service.outstanding_amount,
                allocated_amount: service.allocated_amount,
                allocation_scope: 'CURRENT_TOTAL',
                line_total: Number(service.sale_price || 0) * Number(service.quantity || 0),
                customer_service_id: service.id,
                selected_at: service.selected_at || null,
            }));

            // Gộp hai mảng
            const mergedItems = [...outstandingProductMapped, ...receiptExportMapped, ...servicesMapped];
            // Render các phần tử mới
            this.renderNewItem(mergedItems);
            // Phiếu tạo mới mặc định theo cơ sở hiện tại của khách hàng vừa chọn.
            this.props.form.setFieldsValue({ branch_id: item.branch_id || null });
            this.setState({ isLoading: false, customer_debt: item.debt, customer_balance: Number(item.unallocated_balance || 0) }, () => {
                this.calculateNetTotal(); // Tính toán lại net_total
            });
            if (checkPermission('customer_deposit', 'apply')) {
                this.props.getCustomerDepositSummary(id).then(depositData => {
                    const nextDepositData = depositData || {};
                    const availableDeposits = (nextDepositData.active_deposits || [])
                        .filter(deposit => Number(deposit.current_balance || 0) > 0);

                    this.setState({ deposit_data: nextDepositData }, () => {
                        this.props.form.setFieldsValue({
                            deposit_ids: availableDeposits.length === 1
                                ? [Number(availableDeposits[0].id)]
                                : [],
                            deposit_amount: 0,
                        });
                    });
                }).catch(() => this.setState({ deposit_data: {} }));
            }
        }).catch(err => {
            this.setState({ items: [] })
        })

        this.calculateTotalAmount();
    }

    calculateTotalAmount = () => {
        let total_amount = 0;
        (this.state.items || []).forEach(item => {
            total_amount += Number(item.amount || 0);
        });

        this.props.form.setFieldsValue({ total_amount });

        this.calculateNetTotal();
    };

    getProductAmount = (items = this.state.items) => (items || [])
        .filter(item => item.type === 'Sản phẩm')
        .reduce((sum, item) => sum + Number(item.amount_to_pay !== undefined
            && item.amount_to_pay !== null ? item.amount_to_pay : Number(item.price || 0) * Number(item.quantity || 0)), 0);

    getServiceAmount = (items = this.state.items) => (items || [])
        .filter(item => item.type === 'Dịch vụ' && item.customer_service_id)
        .reduce((sum, item) => sum + Math.max(0, Number(item.amount) || 0), 0);

    onChangeFieldTotal = (field, value) => {
        this.props.form.setFieldsValue({ [field]: value });
        if (field === 'total_amount' || field === 'special_discount') {
            this.calculateNetTotal();
        }
    };

    calculateNetTotal = (overrides = {}) => {
        const productAmount = this.getProductAmount();
        const serviceAmount = this.getServiceAmount();
        const special_discount = Number(this.props.form.getFieldValue('special_discount') || 0);
        const balanceAmount = Number(overrides.balance_amount !== undefined
            ? overrides.balance_amount : this.props.form.getFieldValue('balance_amount')) || 0;
        const depositAmount = Number(overrides.deposit_amount !== undefined
            ? overrides.deposit_amount : this.props.form.getFieldValue('deposit_amount')) || 0;
        const net_total = calculateReceiptExternalAmount({
            productAmount,
            serviceAmount,
            specialDiscount: special_discount,
            balanceAmount,
            depositAmount,
        });
        const hasInternalPaymentSource = balanceAmount > 0 || depositAmount > 0
            || hasReceiptInternalPaymentSource({
                balanceAvailable: this.state.customer_balance,
                activeDeposits: this.state.deposit_data.active_deposits,
            });

        this.setState(prevState => {
            let paymentBreakdowns = prevState.payment_breakdowns;
            const emptyPayment = {
                key: `payment-${Date.now()}`,
                payment_method: null,
                amount: 0,
                provider_name: 'VNĐ',
            };
            if (net_total <= 0) {
                paymentBreakdowns = hasInternalPaymentSource
                    ? []
                    : (paymentBreakdowns.length ? paymentBreakdowns : [emptyPayment]);
            } else if (paymentBreakdowns.length <= 1) {
                paymentBreakdowns = [{
                    ...(paymentBreakdowns[0] || emptyPayment),
                    amount: net_total,
                }];
            }
            return {
                special_discount,
                net_total,
                payment_breakdowns: paymentBreakdowns,
            };
        });

        // set trực tiếp, không cần check !== undefined
        this.props.form.setFieldsValue({ net_total });
    };

    addPaymentBreakdown = () => {
        this.setState(state => state.payment_breakdowns.length >= maxPaymentMethods ? null : ({
            payment_breakdowns: [...state.payment_breakdowns, {
                key: `payment-${Date.now()}-${Math.random()}`, payment_method: null, amount: 0,
                provider_name: 'VNĐ',
            }]
        }));
    };

    updatePaymentBreakdown = (key, field, value) => {
        this.setState(state => {
            const payment_breakdowns = state.payment_breakdowns.map(row => row.key === key ? { ...row, [field]: value } : row);
            const net_total = payment_breakdowns.reduce((sum, row) => sum + Number(row.amount || 0), 0);
            this.props.form.setFieldsValue({ net_total });
            return { payment_breakdowns, net_total };
        });
    };

    removePaymentBreakdown = key => {
        this.setState(state => {
            const payment_breakdowns = state.payment_breakdowns.filter(row => row.key !== key);
            const net_total = payment_breakdowns.reduce((sum, row) => sum + Number(row.amount || 0), 0);
            this.props.form.setFieldsValue({ net_total });
            return { payment_breakdowns, net_total };
        });
    };

    handleImageChange = (fileList) => {
        const images = (fileList || []).map((file) => ({
            manual_id: file.manual_id ?? file.uid ?? file.id ?? null,
            src: file.originalImage ?? file.src ?? file.url ?? null,
        })).filter(x => x.src);

        this.setState({ images });
    };

    render() {
        var {
            isLoading, submitting, items, customer_debt, customer_balance, deposit_data, net_total, payment_breakdowns,
        } = this.state;
        var { visible, currentReceipt } = this.props;
        const { getFieldDecorator } = this.props.form;
        const refundSummary = currentReceipt?.refund_summary || {};
        const refundStatus = refundSummary.status || 'NONE';
        const hasCompletedRefund = refundStatus !== 'NONE';
        const isRefundFinancialLocked = refundSummary.can_update_financial === false;
        const refundedAmount = Number(refundSummary.refunded_amount || 0);
        const receiptRefundAmount = Number(refundSummary.receipt_amount || currentReceipt?.amount_paid || 0);
        const refundableAmount = Math.max(0, Number(
            refundSummary.refundable_amount ?? refundSummary.remaining_amount ?? 0
        ));
        const refundStatusMessage = refundSummary.refund_status_message
            || `Đã hoàn ${renderMoney(refundedAmount)} / ${renderMoney(receiptRefundAmount)}`;
        const financialUpdateMessage = refundSummary.financial_update_message
            || (isRefundFinancialLocked ? 'Chỉ có thể sửa ghi chú và hình ảnh.' : null);
        const isDepositPermissionLocked = Boolean(currentReceipt
            && Number(currentReceipt?.deposit_application?.amount || 0) > 0
            && !checkPermission('customer_deposit', 'apply'));
        const isFinancialMetadataOnly = isRefundFinancialLocked || isDepositPermissionLocked;
        const currentAppliedBalance = currentReceipt
            ? Number(currentReceipt?.balance_application?.amount || 0)
            : 0;
        const balancePresentation = calculateEditableBalance({
            available: currentReceipt
                ? Number(currentReceipt.customer_unallocated_balance || 0)
                : Number(customer_balance || 0),
            applied: currentAppliedBalance,
            requested: Number(this.props.form.getFieldValue('balance_amount') || 0),
        });
        const availableBalance = balancePresentation.editableLimit;
        const activeDeposits = (deposit_data.active_deposits || [])
            .filter(deposit => Number(deposit.current_balance || 0) > 0);
        const storedDepositIds = this.props.form.getFieldValue('deposit_ids');
        const selectedDepositIds = Array.isArray(storedDepositIds)
            ? storedDepositIds
            : activeDeposits.length === 1
                ? [Number(activeDeposits[0].id)]
                : [];
        const selectedDepositBalance = getSelectedDepositBalance(selectedDepositIds, activeDeposits);
        const activeDepositBalance = activeDeposits.reduce(
            (sum, deposit) => sum + Number(deposit.current_balance || 0),
            0
        );
        const displayedDepositBalance = selectedDepositIds.length > 0
            ? selectedDepositBalance
            : activeDepositBalance;
        const depositAmount = Number(this.props.form.getFieldValue('deposit_amount') || 0);
        const balanceAmountForSummary = Number(this.props.form.getFieldValue('balance_amount') || 0);
        const serviceAmount = this.getServiceAmount(items);
        const settlementSummary = calculateReceiptSettlement({
            externalAmount: buildReceiptPaymentBreakdowns(payment_breakdowns)
                .reduce((sum, row) => sum + Number(row.amount || 0), 0),
            productAmount: this.getProductAmount(items),
            serviceAmount,
            specialDiscount: this.state.special_discount,
            balanceAmount: balanceAmountForSummary,
            depositAmount,
        });
        const balanceLimit = Math.min(availableBalance, Math.max(0,
            settlementSummary.internalLimit - depositAmount));
        const depositLimit = Math.min(selectedDepositBalance, Math.max(0,
            settlementSummary.internalLimit - balanceAmountForSummary));
        const hasInternalPaymentSource = hasReceiptInternalPaymentSource({
            balanceAvailable: availableBalance,
            activeDeposits,
        });

        var columns = [
            {
                title: "STT",
                dataIndex: "stt",
                width: "40px",
                align: 'center',
                render: (text, record, index) => index + 1
            },
            {
                title: "Tên dịch vụ/Sản phẩm",
                dataIndex: "name",
                width: '300px',
                render: (name, record) => (
                    <ReceiptItemInfoCell
                        name={name}
                        type={record.type}
                        serviceId={record.origin_id}
                        customerServiceId={record.customer_service_id}
                        selectedAt={record.selected_at}
                    />
                )
            },
            {
                title: "Số lượng",
                dataIndex: "quantity",
                width: '80px',
                render: (text, record) => {
                    return (
                        <InputNumber
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            value={text}
                            name="quantity"
                            min={1}
                            className='w-100 fix-height-input'
                            disabled={record.type === 'Dịch vụ' || Boolean(record.product_sale_id)}
                            onChange={(value) => {
                                const validValue = value == null || value <= 0 ? 1 : value;
                                this.onChangeItem('quantity', validValue, record.key);
                            }}
                        />
                    )
                }
            },
            {
                title: "Giá bán",
                dataIndex: "price",
                width: "120px",
                render: (text, record) => {
                    return (
                        <InputNumber
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            value={text}
                            name="price"
                            step={50000}
                            min={0}
                            disabled={record.type === 'Dịch vụ' || Boolean(record.product_sale_id)}
                            className='w-100 fix-height-input'
                            onChange={(value) => this.onChangeItem('price', value, record.key)}
                        />
                    )
                }
            },
            {
                title: "Thành tiền",
                dataIndex: "line_total",
                width: "120px",
                render: (_, record) => <>{renderMoney(Number(record.price || 0) * Number(record.quantity || 0))}</>
            },
            ...(!currentReceipt ? [
                {
                    title: fieldLabel(
                        'Đã phân bổ',
                        this.props.currentReceipt
                            ? 'Số tiền được phân bổ cho dịch vụ/sản phẩm ngay tại phiếu đang sửa. Không cộng các phiếu khác.'
                            : 'Số tiền đã được ghi nhận cho dịch vụ/sản phẩm từ các lần thanh toán trước.'
                    ),
                    dataIndex: "allocated_amount",
                    width: "120px",
                    render: (value, record) => {
                        const allocatedAmount = Math.max(0, Number(value || 0));

                        if (record.allocation_scope !== 'RECEIPT') {
                            return <>{renderMoney(allocatedAmount)}</>;
                        }

                        const effectiveAmount = Math.max(
                            0, Number(record.effective_allocated_amount || 0)
                        );
                        const refundedAmount = Math.max(
                            0, Number(record.refunded_allocated_amount || 0)
                        );

                        return (
                            <Tooltip
                                title={
                                    <div style={{ lineHeight: 1.7 }}>
                                        <div>
                                            Tổng tại phiếu: <strong>{renderMoney(allocatedAmount)}</strong>
                                        </div>
                                        <div>
                                            Còn hiệu lực: <strong>{renderMoney(effectiveAmount)}</strong>
                                        </div>
                                        {refundedAmount > 0 && (
                                            <div style={{ color: '#ff7875' }}>
                                                Hoàn/điều chỉnh: {renderMoney(refundedAmount)}
                                            </div>
                                        )}
                                    </div>
                                }
                            >
                                <span style={{ cursor: 'help', whiteSpace: 'nowrap' }}>
                                    <strong>{renderMoney(allocatedAmount)}</strong>
                                    {' '}
                                    <QuestionCircleOutlined
                                        style={{ color: '#8c8c8c', fontSize: 11 }}
                                    />
                                </span>
                            </Tooltip>
                        );
                    }
                },
            ] : []),
            {
                title: fieldLabel(
                    'Số tiền cần thu',
                    'Số tiền khách còn cần thanh toán cho dịch vụ này tại thời điểm lập phiếu.'
                ),
                dataIndex: "outstanding_amount",
                width: "140px",
                render: (_, record) => {
                    let amountToCollect = record.outstanding_amount;
                    if (amountToCollect === undefined || amountToCollect === null) {
                        amountToCollect = record.amount_to_pay;
                    }
                    if (amountToCollect === undefined || amountToCollect === null) {
                        amountToCollect = record.amount || 0;
                    }
                    return <>{renderMoney(Number(amountToCollect))}</>;
                }
            },
            {
                title: "#",
                align: "center",
                width: '30px',
                render: (text, record) => {
                    return (
                        <Popconfirm
                            title="Xóa bản ghi này?"
                            placement='left'
                            onConfirm={() => this.onDelete(record.key)}
                        >
                            <DeleteOutlined className='item-action-btn-remove' />
                        </Popconfirm>
                    )
                }
            }
        ];

        var total_amount = 0;
        items.forEach(item => {
            total_amount += Number(item.amount || 0);
        });
        const availableCustomerServices = getAvailableCustomerServices(
            this.state.available_customer_services,
            items
        );

        return (
            <Drawer
                visible={visible}
                title={isFinancialMetadataOnly ? 'Cập nhật ghi chú / ảnh phiếu thu' : currentReceipt ? 'Sửa phiếu thu' : `Tạo phiếu thu`}
                onOk={(e) => this.submit(e)}
                onClose={() => this.onClose()}
                maskClosable={false}
                width={isMobile ? '100%' : '70%'}
                footer={
                    <div style={{ textAlign: 'right', }}>
                        <Space>
                            <Button size='large' key="back" onClick={() => this.onClose()}>
                                Huỷ
                            </Button>
                            <Button
                                loading={submitting}
                                key="submit"
                                type="primary"
                                size="large"
                                onClick={(e) => this.submit(e)}
                            >
                                {isFinancialMetadataOnly ? 'Lưu ghi chú / ảnh' : currentReceipt ? 'Cập nhật' : "Tạo phiếu"}
                            </Button>
                        </Space>
                    </div>
                }
            >
                {visible ?
                    <Form layout='vertical' className={`receipt-form${isFinancialMetadataOnly ? ' receipt-form--refund-locked' : ''}`}>
                        {hasCompletedRefund && (
                            <Alert
                                className="mb-3"
                                type={isRefundFinancialLocked ? 'error' : 'warning'}
                                message={refundStatusMessage}
                                description={refundStatus !== 'FULL' || financialUpdateMessage ? (
                                    <div>
                                        {refundStatus !== 'FULL' && (
                                            <div>Còn có thể hoàn: <strong>{renderMoney(refundableAmount)}</strong></div>
                                        )}
                                        {financialUpdateMessage && <div>{financialUpdateMessage}</div>}
                                    </div>
                                ) : null}
                            />
                        )}
                        {hasCompletedRefund && !isRefundFinancialLocked && (
                            <Form.Item label="Lý do chỉnh sửa tài chính">
                                {getFieldDecorator('mutation_reason', {
                                    rules: [
                                        { required: true, whitespace: true, message: 'Vui lòng nhập lý do chỉnh sửa tài chính.' },
                                        { max: 500, message: 'Lý do không được vượt quá 500 ký tự.' },
                                    ],
                                })(
                                    <Input.TextArea
                                        rows={3}
                                        maxLength={500}
                                        placeholder="Nêu rõ lý do cần điều chỉnh phiếu sau khi đã hoàn tiền"
                                    />
                                )}
                            </Form.Item>
                        )}
                        {isDepositPermissionLocked && (
                            <Alert
                                className="mb-3"
                                type="warning"
                                message="Phiếu đang sử dụng tiền cọc"
                                description="Bạn chưa có quyền sử dụng tiền cọc nên chỉ có thể cập nhật ghi chú và ảnh. Cần quyền Sử dụng tiền cọc để thay đổi nội dung tài chính của phiếu."
                            />
                        )}
                        <div className="receipt-form__hidden-fields">
                            {getFieldDecorator('balance_reason', {
                                initialValue: currentReceipt
                                    ? 'Sử dụng số dư khi cập nhật phiếu thu.'
                                    : 'Sử dụng số dư khi tạo phiếu thu.',
                            })(<Input type="hidden" />)}
                            {getFieldDecorator('deposit_reason', {
                                initialValue: currentReceipt
                                    ? 'Sử dụng tiền cọc khi cập nhật phiếu thu.'
                                    : 'Sử dụng tiền cọc khi tạo phiếu thu.',
                            })(<Input type="hidden" />)}
                        </div>
                        <Spin tip="Loading..." spinning={isLoading}>
                            <div className="receipt-form__sections">
                                <fieldset
                                    disabled={isFinancialMetadataOnly}
                                    style={{
                                        border: 0,
                                        margin: 0,
                                        padding: 0,
                                        minWidth: 0,
                                        opacity: isFinancialMetadataOnly ? 0.62 : 1,
                                        pointerEvents: isFinancialMetadataOnly ? 'none' : 'auto',
                                    }}
                                >
                                    <Row gutter={16}>
                                        {
                                            currentReceipt ? (
                                                <Col xs={24}>
                                                    <div className="receipt-form__customer-summary">
                                                        <UserOutlined />
                                                        <span>Khách hàng: <strong className='color-primary'>
                                                            {currentReceipt?.customer?.full_name + ' - ' + currentReceipt?.customer?.code}
                                                        </strong></span>
                                                    </div>
                                                </Col>
                                            ) : (
                                                <Col xs={24} md={24}>
                                                    <Form.Item label={fieldLabel('Khách hàng', 'Chọn người thực tế nộp tiền. Hệ thống dùng khách hàng này để tải đúng công nợ dịch vụ và số dư chưa phân bổ.')}>
                                                        {getFieldDecorator("customer_id", {
                                                            rules: [
                                                                {
                                                                    required: true,
                                                                    message: "Vui lòng chọn!",
                                                                },
                                                            ],
                                                            initialValue: currentReceipt ? currentReceipt?.customer_id : null
                                                        })(
                                                            <CustomerSelect
                                                                onChange={(id, label) => this.onChangeCustomer(id, label)}
                                                            />
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                            )
                                        }
                                    </Row>
                                    {currentReceipt && (
                                        <Row gutter={16}>
                                            <Col xs={24}>
                                                <Form.Item label={fieldLabel('Thêm dịch vụ', 'Chỉ hiển thị dịch vụ còn công nợ của đúng khách hàng và chưa có trong phiếu thu.')}>
                                                    <Space className="w-100" align="start">
                                                        <Select
                                                            mode="multiple"
                                                            showSearch
                                                            allowClear
                                                            loading={this.state.service_options_loading}
                                                            value={this.state.selected_customer_service_ids}
                                                            placeholder="Chọn một hoặc nhiều dịch vụ"
                                                            optionFilterProp="children"
                                                            style={{ minWidth: isMobile ? '65vw' : '45vw' }}
                                                            onChange={selected_customer_service_ids => this.setState({ selected_customer_service_ids })}
                                                        >
                                                            {availableCustomerServices.map(service => (
                                                                <Select.Option key={service.id} value={Number(service.id)}>
                                                                    {(service.service?.name || service.service_name || `Dịch vụ #${service.id}`)} — còn {renderMoney(Number(service.outstanding_amount || 0))}
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                        <Button
                                                            type="dashed"
                                                            disabled={!this.state.selected_customer_service_ids.length}
                                                            onClick={this.addSelectedCustomerServices}
                                                        >
                                                            Thêm
                                                        </Button>
                                                    </Space>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    )}
                                    <Row gutter={16}>
                                        <Col xs={24}>
                                            <Form.Item label={fieldLabel('Sản phẩm', 'Chọn sản phẩm xuất kèm phiếu nếu có; số lượng và giá bán được đưa vào nội dung thu nhưng không thay đổi nghĩa vụ của dịch vụ.')}>
                                                <ProductSelect
                                                    defaultText="Chọn sản phẩm"
                                                    optionValue="id"
                                                    optionLabel="name"
                                                    additionalLabel1="quantity"
                                                    additionalLabel2="expired_at"
                                                    additionalLabel3="warehouse_name"
                                                    onChange={(id, label) => this.onChangeProduct(id, label)}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col xs={24}>
                                            <Table
                                                className='receipt-form__table desabled-input-table'
                                                size='small'
                                                rowKey='key'
                                                locale={{ emptyText: "Chưa có dữ liệu" }}
                                                tableLayout="auto"
                                                columns={columns}
                                                dataSource={items || []}
                                                pagination={false}
                                                scroll={{ x: 'max-content' }}
                                            />
                                        </Col>
                                    </Row>
                                    <div className="receipt-form__financial-fields">
                                        <Row gutter={16} justify="end">
                                            <Col xs={24} sm={24} md={24}>
                                                <Form.Item
                                                    className="form-item-label-right"
                                                    label={fieldLabel('Tổng cần thu theo nội dung', 'Tổng các cột Số tiền cần thu của nội dung đang chọn; hệ thống tự tính và không yêu cầu nhập lại.')}
                                                    labelCol={{ xs: { span: 24 }, sm: { span: 10 }, md: { span: 14 }, lg: { span: 16 }, xl: { span: 18 } }}
                                                    wrapperCol={{ xs: { span: 24 }, sm: { span: 14 }, md: { span: 10 }, lg: { span: 8 }, xl: { span: 6 } }}
                                                >
                                                    {getFieldDecorator('total_amount', {
                                                        rules: [
                                                            {
                                                                required: false,
                                                                message: 'Vui lòng điền!',
                                                            },
                                                        ],
                                                        initialValue: currentReceipt ? currentReceipt.total_amount : total_amount
                                                    })(
                                                        <InputNumber
                                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                            className="w-100"
                                                            step={50000}
                                                            min={0}
                                                            disabled
                                                            onChange={(value) => this.onChangeFieldTotal('total_amount', value)}
                                                        />
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16} justify="end">
                                            <Col xs={24} sm={24} md={24}>
                                                <Form.Item
                                                    className="form-item-label-right"
                                                    label={fieldLabel('Tổng thanh toán lần này', 'Tự động bằng tiền khách thực nộp mới cộng tiền cọc và số dư sử dụng trong lần này.')}
                                                    labelCol={{ xs: { span: 24 }, sm: { span: 10 }, md: { span: 14 }, lg: { span: 16 }, xl: { span: 18 } }}
                                                    wrapperCol={{ xs: { span: 24 }, sm: { span: 14 }, md: { span: 10 }, lg: { span: 8 }, xl: { span: 6 } }}
                                                >
                                                    <InputNumber disabled value={settlementSummary.settlementTotal}
                                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} className="w-100" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16} justify="end">
                                            <Col xs={24} sm={24} md={24}>
                                                <Form.Item
                                                    className="form-item-label-right"
                                                    label={fieldLabel('Công nợ hiện tại', 'Tổng nghĩa vụ dịch vụ còn lại của khách trước giao dịch này; tiền thu vượt không làm công nợ âm mà chuyển thành số dư chưa phân bổ.')}
                                                    labelCol={{ xs: { span: 24 }, sm: { span: 10 }, md: { span: 14 }, lg: { span: 16 }, xl: { span: 18 } }}
                                                    wrapperCol={{ xs: { span: 24 }, sm: { span: 14 }, md: { span: 10 }, lg: { span: 8 }, xl: { span: 6 } }}
                                                >
                                                    {getFieldDecorator('outstanding_debt', {
                                                        rules: [
                                                            {
                                                                required: false,
                                                                message: "Vui lòng điền!",
                                                            },
                                                        ],
                                                        initialValue: Math.max(0, Number(currentReceipt ? currentReceipt.remaining_debt ?? 0 : customer_debt) || 0)
                                                    })(
                                                        <InputNumber
                                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                            className="w-100"
                                                            step={50000}
                                                            min={0}
                                                            disabled
                                                            placeholder='Đơn vị vnđ'
                                                        />
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16} justify="end">
                                            <Col xs={24} sm={24} md={24}>
                                                <Form.Item
                                                    className="form-item-label-right"
                                                    label={fieldLabel('Ưu đãi đặc biệt', 'Khoản giảm nghĩa vụ được phân bổ cho các dịch vụ trong phiếu; nhập đúng căn cứ vì thay đổi này ảnh hưởng công nợ và doanh thu đủ điều kiện.')}
                                                    labelCol={{ xs: { span: 24 }, sm: { span: 10 }, md: { span: 14 }, lg: { span: 16 }, xl: { span: 18 } }}
                                                    wrapperCol={{ xs: { span: 24 }, sm: { span: 14 }, md: { span: 10 }, lg: { span: 8 }, xl: { span: 6 } }}
                                                >
                                                    {getFieldDecorator('special_discount', {
                                                        rules: [
                                                            {
                                                                required: true,
                                                                message: "Vui lòng điền!",
                                                            },
                                                        ],
                                                        initialValue: currentReceipt ? currentReceipt.special_discount : 0
                                                    })(
                                                        <InputNumber
                                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                            className="w-100"
                                                            step={50000}
                                                            min={0}
                                                            placeholder='Đơn vị vnđ'
                                                            onChange={(value) => this.onChangeFieldTotal('special_discount', value)}
                                                        />
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16} justify="end">
                                            <Col xs={24} sm={24} md={24}>
                                                <Form.Item
                                                    className="form-item-label-right"
                                                    label={fieldLabel('Khách thực nộp mới', 'Chỉ gồm tiền mặt/chuyển khoản/thanh toán thẻ khách nộp thêm; không bao gồm tiền cọc hoặc số dư đã có.')}
                                                    labelCol={{ xs: { span: 24 }, sm: { span: 10 }, md: { span: 14 }, lg: { span: 16 }, xl: { span: 18 } }}
                                                    wrapperCol={{ xs: { span: 24 }, sm: { span: 14 }, md: { span: 10 }, lg: { span: 8 }, xl: { span: 6 } }}
                                                >
                                                    {getFieldDecorator('net_total', { initialValue: currentReceipt ? currentReceipt.amount_paid : net_total })(
                                                        <InputNumber disabled formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} className="w-100" />
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        {/* <Row gutter={16} justify="end">
                                            <Col xs={24} sm={24} md={24}>
                                                <Form.Item
                                                    className="form-item-label-right"
                                                    label={fieldLabel('Dự kiến khấu trừ công nợ', 'Tính theo cùng quy tắc phân bổ backend; sản phẩm không được tính vào công nợ dịch vụ.')}
                                                    labelCol={{ xs: { span: 24 }, sm: { span: 10 }, md: { span: 14 }, lg: { span: 16 }, xl: { span: 18 } }}
                                                    wrapperCol={{ xs: { span: 24 }, sm: { span: 14 }, md: { span: 10 }, lg: { span: 8 }, xl: { span: 6 } }}
                                                >
                                                    <InputNumber disabled value={settlementSummary.debtReductionAmount}
                                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} className="w-100" />
                                                </Form.Item>
                                            </Col>
                                        </Row> */}
                                    </div>

                                    <ReceiptSection
                                        icon={<CreditCardOutlined />}
                                        title="Hình thức thu tiền mới"
                                        description="Chỉ khai báo khi khách nộp thêm tiền; tiền cọc và số dư được ghi nhận riêng."
                                        financial
                                        extra={<Button size="small" icon={<PlusOutlined />}
                                            disabled={payment_breakdowns.length >= maxPaymentMethods}
                                            onClick={this.addPaymentBreakdown}>
                                            Thêm hình thức ({payment_breakdowns.length}/{maxPaymentMethods})
                                        </Button>}
                                    >
                                        {settlementSummary.externalAmount <= 0 && hasInternalPaymentSource && (
                                            <Alert
                                                className="mb-3"
                                                type="info"
                                                message="Chưa có tiền khách nộp thêm"
                                                description="Nếu chỉ sử dụng tiền cọc hoặc số dư, bạn không cần chọn hình thức thu."
                                            />
                                        )}
                                        {payment_breakdowns.map((row, index) => {
                                            const usedMethods = payment_breakdowns.filter(item => item.key !== row.key).map(item => item.payment_method);
                                            return <Row className="receipt-payment-row" gutter={8} key={row.key} align="middle">
                                                <Col xs={20} md={11}><Form.Item label={index === 0 ? 'Hình thức thanh toán' : ''} required={Number(row.amount || 0) > 0}>
                                                    <Select allowClear value={row.payment_method} placeholder="Chọn hình thức" onChange={value => this.updatePaymentBreakdown(row.key, 'payment_method', value || null)}
                                                        options={(paymentMethodOptions || []).filter(item => !usedMethods.includes(item.value)).map(item => ({ value: item.value, label: item.label }))} />
                                                </Form.Item></Col>
                                                <Col xs={20} md={11}><Form.Item label={index === 0 ? 'Số tiền' : ''} required={Number(row.amount || 0) > 0}>
                                                    <InputNumber min={0} precision={0} step={50000} value={row.amount} className="w-100"
                                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                        onChange={value => this.updatePaymentBreakdown(row.key, 'amount', Number(value || 0))} />
                                                </Form.Item></Col>
                                                <Col className="receipt-payment-row__remove" xs={4} md={2}><Button danger type="text" icon={<DeleteOutlined />}
                                                    onClick={() => this.removePaymentBreakdown(row.key)} /></Col>
                                            </Row>;
                                        })}
                                    </ReceiptSection>
                                    {(availableBalance > 0 || activeDeposits.length > 0) && (
                                        <Row className="receipt-payment-sources" gutter={[16, 16]} align="stretch">
                                            {availableBalance > 0 && (
                                                <Col className="receipt-payment-sources__col" xs={24} md={12}>
                                                    <PaymentSourceBox
                                                        icon={<WalletOutlined />}
                                                        title="Số dư khách hàng"
                                                        available={balancePresentation.actualAvailable}
                                                        remaining={balancePresentation.projectedRemaining}
                                                        target="Sản phẩm/dịch vụ còn phải thanh toán trong phiếu"
                                                        tone="balance"
                                                    >
                                                        {getFieldDecorator('balance_customer_service_id', {
                                                            initialValue: 'ALL',
                                                        })(<Input type="hidden" />)}

                                                        <Form.Item className="receipt-payment-source__form-item" label="Số tiền sử dụng">
                                                            <div className="receipt-payment-source__amount-row">
                                                                {getFieldDecorator('balance_amount', {
                                                                    initialValue: 0,
                                                                })(
                                                                    <InputNumber
                                                                        min={0}
                                                                        max={balanceLimit}
                                                                        precision={0}
                                                                        step={50000}
                                                                        className="receipt-payment-source__amount-input"
                                                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                                        onChange={value => this.calculateNetTotal({ balance_amount: Number(value || 0) })}
                                                                    />
                                                                )}
                                                                <Button
                                                                    htmlType="button"
                                                                    onClick={() => {
                                                                        this.props.form.setFieldsValue({ balance_amount: balanceLimit });
                                                                        this.calculateNetTotal({ balance_amount: balanceLimit });
                                                                    }}
                                                                >
                                                                    Dùng hết
                                                                </Button>
                                                            </div>
                                                        </Form.Item>
                                                    </PaymentSourceBox>
                                                </Col>
                                            )}

                                            {activeDeposits.length > 0 && (
                                                <Col className="receipt-payment-sources__col" xs={24} md={12}>
                                                    <PaymentSourceBox
                                                        icon={<BankOutlined />}
                                                        title="Tiền cọc"
                                                        available={displayedDepositBalance}
                                                        remaining={Math.max(0, displayedDepositBalance - depositAmount)}
                                                        target="Sản phẩm/dịch vụ còn phải thanh toán trong phiếu"
                                                        tone="deposit"
                                                    >
                                                        {activeDeposits.length > 1 ? (
                                                            <Form.Item className="receipt-payment-source__form-item" label="Khoản cọc">
                                                                {getFieldDecorator('deposit_ids', {
                                                                    initialValue: selectedDepositIds,
                                                                    rules: [{
                                                                        validator: (_, value, callback) => {
                                                                            callback(depositAmount > 0 && (!value || value.length === 0)
                                                                                ? 'Vui lòng chọn ít nhất một khoản cọc.'
                                                                                : undefined);
                                                                        }
                                                                    }]
                                                                })(
                                                                    <Select
                                                                        mode="multiple"
                                                                        showSearch
                                                                        optionFilterProp="label"
                                                                        placeholder="Chọn khoản cọc"
                                                                        style={{ width: '100%' }}
                                                                        options={activeDeposits.map(deposit => ({
                                                                            value: Number(deposit.id),
                                                                            label: `${deposit.receipt_code || `Cọc #${deposit.id}`} - có thể dùng ${renderMoney(deposit.current_balance)}`,
                                                                        }))}
                                                                        onChange={() => {
                                                                            this.props.form.setFieldsValue({ deposit_amount: 0 });
                                                                            this.calculateNetTotal({ deposit_amount: 0 });
                                                                        }}
                                                                    />
                                                                )}
                                                            </Form.Item>
                                                        ) : (
                                                            getFieldDecorator('deposit_ids', {
                                                                initialValue: selectedDepositIds,
                                                            })(<Input type="hidden" />)
                                                        )}

                                                        <Form.Item className="receipt-payment-source__form-item" label="Số tiền sử dụng">
                                                            <div className="receipt-payment-source__amount-row">
                                                                {getFieldDecorator('deposit_amount', {
                                                                    initialValue: 0,
                                                                    rules: [{
                                                                        validator: (_, value, callback) => {
                                                                            const amount = Number(value || 0);
                                                                            callback(amount > depositLimit
                                                                                ? 'Số tiền vượt quá khoản cọc có thể sử dụng cho phiếu này.'
                                                                                : undefined);
                                                                        }
                                                                    }]
                                                                })(
                                                                    <InputNumber
                                                                        min={0}
                                                                        max={depositLimit}
                                                                        precision={0}
                                                                        step={50000}
                                                                        className="receipt-payment-source__amount-input"
                                                                        disabled={selectedDepositIds.length === 0}
                                                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                                        onChange={value => this.calculateNetTotal({ deposit_amount: Number(value || 0) })}
                                                                    />
                                                                )}
                                                                <Button
                                                                    htmlType="button"
                                                                    disabled={selectedDepositIds.length === 0 || depositLimit <= 0}
                                                                    onClick={() => {
                                                                        this.props.form.setFieldsValue({ deposit_amount: depositLimit });
                                                                        this.calculateNetTotal({ deposit_amount: depositLimit });
                                                                    }}
                                                                >
                                                                    Dùng hết
                                                                </Button>
                                                            </div>
                                                        </Form.Item>
                                                    </PaymentSourceBox>
                                                </Col>
                                            )}
                                        </Row>
                                    )}
                                </fieldset>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label={fieldLabel('Cơ sở thu tiền', 'Chọn đúng nơi thực tế nhận tiền; giá trị này được lưu cố định cho lịch sử và đối soát.')}>
                                            {getFieldDecorator('branch_id', {
                                                rules: [{ required: true, message: 'Vui lòng chọn cơ sở thực tế thu tiền.' }],
                                                initialValue: currentReceipt ? currentReceipt.branch_id : null
                                            })(<Select disabled={isFinancialMetadataOnly} showSearch optionFilterProp="label" options={(this.props.config.branches || []).map(branch => ({ value: branch.id, label: branch.name }))} />)}
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item label={fieldLabel('Ngày thu', 'Ngày thực tế nhận tiền, dùng làm thời điểm phân bổ và xác định chính sách hoa hồng có hiệu lực.')}>
                                            {getFieldDecorator('payment_at', {
                                                rules: [
                                                    {
                                                        required: true,
                                                        message: 'Vui lòng chọn!',
                                                    },
                                                ],
                                                initialValue: currentReceipt ? isValidDateForm(currentReceipt.payment_at, false) : moment()
                                            })(
                                                <DatePicker
                                                    className='w-100'
                                                    format="DD/MM/YYYY"
                                                    disabled={isFinancialMetadataOnly}
                                                />
                                            )}
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item label={fieldLabel('Ghi chú', 'Bổ sung nội dung thu hoặc căn cứ ưu đãi để người dùng khác có thể kiểm tra giao dịch.')}>
                                            {getFieldDecorator('note', {
                                                rules: [
                                                    {
                                                        required: false,
                                                        message: "Vui lòng điền!",
                                                    },
                                                ],
                                                initialValue: currentReceipt ? _newLine(currentReceipt.note) : null
                                            })(
                                                <Input.TextArea rows={3} />
                                            )}
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item label={fieldLabel(<><PaperClipOutlined /> Ảnh phiếu</>, 'Đính kèm ảnh chứng từ thu để phục vụ tra cứu và đối chiếu; không tải lên thông tin không liên quan.')} className='flex-upload'>
                                            <ImageSelector
                                                key={currentReceipt?.id || 'new'}
                                                onChangeData={(fileList, changeDetails) => this.handleImageChange(fileList, changeDetails)} // Gắn hàm xử lý thay đổi dữ liệu
                                                multiple={true} // Cho phép chọn nhiều ảnh
                                                maxCount={50} // Giới hạn tối đa 50 ảnh
                                                uploadText="Chọn ảnh"
                                                defaultFileList={currentReceipt ? currentReceipt.images : []}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        </Spin>
                    </Form>
                    : null
                }
            </Drawer>
        )
    }
}

function mapStateToProps(state) {
    return {
        config: state.config,
        users: state.config.users,
        authUser: state.auth.authUser,
    }
}

function mapDispatchToProps(dispatch) {
    return {
        createReceipt: (data) => dispatch(createReceipt(data)),
        updateReceipt: (id, data) => dispatch(updateReceipt(id, data)),
        previewReceiptMutation: (id, data) => dispatch(previewReceiptMutation(id, data)),
        fetchProduct: (id) => dispatch(fetchProduct(id)),
        fetchCustomer: (id, filter) => dispatch(fetchCustomer(id, filter)),
        getCustomerDepositSummary: (id) => dispatch(getCustomerDepositSummary(id))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Form.create({ name: 'ReceiptForm' })(ReceiptForm));
